// src/portal/PortalFindingForm.jsx
//
// THE REVIEWER'S FINDING. A feature, a type, a note.
//
// ── GEOMETRY IS OPTIONAL, AND THAT IS WHY THE FORM CAME FIRST ──────────────
//
// `geom` is OPTIONAL on route 9 — checkMarkupInput says "null is the ordinary
// case: most findings are about the feature as submitted". So the minimum
// viable finding is a selection, one of five types, and ten characters. The
// reviewer page rendered "Waiting on you. Add findings, then return the plan or
// approve it" for a week while offering none of the three.
//
// Drawing is an enhancement to a cycle that completes without it, which is why
// it is the last commit of this phase rather than the interesting first one.
//
// ── THE NOTE MINIMUM IS SHOWN, NOT ENFORCED BY REFUSAL ──────────────────────
//
// `note_too_short` exists and has copy, but a reviewer nine characters in
// should be told by a counter, not by a round trip. noteIsLongEnough applies
// THE SAME RULE route 9 applies — btrim, then length — so the two cannot
// disagree about what counts.
//
// ── ONE FEATURE MAY CARRY SEVERAL FINDINGS ──────────────────────────────────
//
// Route 9 allocates ordinal as max+1 per REVISION, so nothing stops a pad
// having both a setback violation and a missing survey, and nothing should: they
// are different remarks. The form does not prevent it and does not warn about
// it. What it does is stay open on the same feature after a successful write,
// because the reviewer who just found one thing is the most likely person to
// find the second.

import { useState } from "react";
import { createMarkup } from "../api/portalApi";
import { dispositionFor } from "../api/portalRefusals";
import { copyFor } from "../api/portalRefusalCopy";
import { MARKUP_TYPES, MARKUP_LABEL, MARKUP_NOTE_MIN, noteIsLongEnough } from "./portalMarkup";

/**
 * What to CALL the geometry in front of the reviewer.
 *
 * This was an inline two-arm ternary — Polygon => "area", ANYTHING ELSE =>
 * "line" — written when a finding could only carry those two. Placement added a
 * third, and a placed note reported itself as "A drawn line will be attached",
 * which is a sentence that is simply false about what is on the map.
 *
 * The acceptance test for placement matched /will be attached/i, so it passed
 * against the wrong noun. A regex loose enough to survive a copy change is not
 * asserting the copy.
 *
 * An unknown type says "shape" rather than guessing, because the next geometry
 * added here should read as vague, not as wrong.
 */
export function geomNoun(geom) {
  if (geom?.type === "Polygon" || geom?.type === "MultiPolygon") return "drawn area";
  if (geom?.type === "LineString" || geom?.type === "MultiLineString") return "drawn line";
  if (geom?.type === "Point") return "placed note";
  return "drawn shape";
}

export default function PortalFindingForm({ planReviewId, feature, geom = null, onWritten }) {
  const [type, setType] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);

  // ── A HELD SHAPE IS NOT NOTHING, AND THE EMPTY STATE SAID IT WAS ────────
  //
  // Route 9 requires feature_id, so a finding always has a subject and this
  // branch is right to refuse. What it got wrong is that it answered the same
  // way whether or not the reviewer had already produced geometry.
  //
  // Placing a note with no feature selected put a pin on the map and left one
  // generic sentence under it — no note field, and nothing connecting the two.
  // A reviewer reads that as "the tool did not work", which is what was
  // reported. The geometry is held and Order A is legitimate (§3.29: place or
  // draw first, then say what it is about), so the empty state now says which
  // of the two situations this is and what closes it.
  if (!feature) {
    return (
      <p className="text-xs text-fg-secondary" data-testid="finding-form-empty">
        {geom
          ? `Your ${geomNoun(geom)} is held. Click the shape on the plan it is about, and the note field opens here.`
          : "Select a shape to record a finding against it."}
      </p>
    );
  }

  const trimmed = note.trim();
  const longEnough = noteIsLongEnough(note);
  const ready = Boolean(type) && longEnough && !busy;

  async function submit() {
    setBusy(true);
    setFailure(null);
    try {
      await createMarkup(planReviewId, {
        featureId: feature.id, markupType: type, note: trimmed,
        // The reviewer's own annotation, if they drew one. OPTIONAL by design --
        // checkMarkupInput: "null is the ordinary case: most findings are about
        // the feature as submitted". Sent explicitly rather than omitted, so the
        // request says what it means either way.
        geom,
      });
      setNote("");
      setType(null);
      // THE 201 IS A SUCCESS SIGNAL, NOT A DATA SOURCE — it echoes the witness
      // columns route 6 withholds (decision 16). The page re-reads.
      onWritten?.();
    } catch (e) {
      setFailure(copyFor(e, dispositionFor(e)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2" data-testid="finding-form">
      <p className="text-xs text-fg-secondary">
        Finding against <strong>#{feature.ordinal}</strong>
        {feature.label ? ` · ${feature.label}` : ""}
      </p>

      {/* ── WHETHER A DRAWN SHAPE IS ATTACHED, AND WHAT BECOMES OF IT ───────
          Geometry is optional, so silence about it is ambiguous: a reviewer who
          drew an area needs to know it is going with the finding, and one who
          did not needs to know nothing is missing.

          The discard rule is stated BEFORE it happens rather than explained
          afterwards. Selecting a different shape drops an unattached drawing,
          because a shape drawn about #0 is not a finding about #1. */}
      <p className="text-[11px] text-fg-secondary" data-testid="finding-geom-state">
        {geom
          ? `Your ${geomNoun(geom)} will be attached to this finding.`
          : "No drawn shape — this finding will refer to the feature as submitted."}
      </p>

      {failure && (
        <div role="alert" className="rounded border border-bg-border bg-bg-soft px-3 py-2">
          <p className="text-sm font-medium">{failure.title}</p>
          {failure.detail && <p className="mt-1 text-xs text-fg-secondary">{failure.detail}</p>}
        </div>
      )}

      {/* Generated from MARKUP_TYPES, imported from the server. A sixth type
          added there appears here without an edit; a type typed here would be a
          second record of decision 9, which the design doc does not carry. */}
      <div className="flex flex-wrap gap-2" data-testid="finding-types">
        {MARKUP_TYPES.map((t) => (
          <button
            key={t}
            type="button"
            data-testid={`finding-type-${t}`}
            aria-pressed={type === t}
            onClick={() => setType(t)}
            className={`rounded border border-bg-border px-2 py-1 text-xs ${type === t ? "bg-bg-soft" : ""}`}
          >
            {MARKUP_LABEL[t] ?? t}
          </button>
        ))}
      </div>

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        placeholder="What needs to change, and why…"
        data-testid="finding-note"
        className="rounded border border-bg-border bg-bg-soft px-3 py-2 text-sm"
      />

      <p className="text-[11px] text-fg-secondary" data-testid="finding-note-count">
        {longEnough
          ? `${trimmed.length} characters`
          : `${trimmed.length} of ${MARKUP_NOTE_MIN} characters minimum — a finding the submitter cannot act on is not a finding.`}
      </p>

      <button
        type="button"
        onClick={submit}
        disabled={!ready}
        data-testid="finding-submit"
        className="self-start rounded border border-bg-border px-2 py-1 text-xs disabled:opacity-50"
      >
        {busy ? "Recording…" : "Record finding"}
      </button>
    </div>
  );
}
